import styles from './TaskChatHeader.module.css'
import {Button, PageHeader, RelativeTime} from '@primer/react'
import type {Session} from '../../types/session'
import {CommentIcon, CopilotIcon} from '@primer/octicons-react'
import type {Pull} from '../../types/pull'
import pluralize from 'pluralize'
import {useCurrentUser} from '../../contexts/CurrentUserContext'
import {useFeedbackDialog} from './feedback/FeedbackButton'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useAgentInfo} from '../../hooks/use-agent-info'
import {useTaskAuthor} from '../../hooks/use-task-author'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {useSelectedModel} from '../../hooks/use-selected-model'
import type {HydratedAPITask} from '../../types/api-task'
import {TaskActions} from '../TaskActions'
import {getTaskTitle} from '../../utils/task-helpers'
import {useState, useRef, useMemo, useCallback} from 'react'
import {getLeadingVisualIconAttributes} from '../../utils/get-leading-visual-icon-attributes'
import {Icon} from '@github-ui/pacer/Icon'
import {isCopilotCodingAgent} from '../../utils/agent-helpers'
import {useAgentProfile} from '../../contexts/AgentProfileContext'
import {ChecksStatusButton} from './ChecksStatusButton'
import {OpenInMenuV2} from './OpenInMenuV2'
import {PullRequestButton} from './PullRequestButton'
import {getBranchArtifactData} from '../../utils/task-artifact-helpers'
import {RenameTaskHeader} from './RenameTaskHeader'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {TaskActionItem} from '../../hooks/use-task-action-items'
import {useIsLayoutNarrow} from './use-is-layout-narrow'

interface TaskChatHeaderProps {
  sessions: Session[]
  currentSessionId?: string
  pull?: Pull
  task?: HydratedAPITask
  repoOwner?: string
  repoName?: string
  hideArchive?: boolean
  isCodeReviewMode?: boolean
  dataUpdatedAt?: number
}

export function TaskChatHeader({
  sessions,
  currentSessionId,
  pull,
  task,
  repoOwner,
  repoName,
  hideArchive = false,
  isCodeReviewMode = false,
  dataUpdatedAt,
}: TaskChatHeaderProps) {
  const mostRecentSession = sessions[sessions.length - 1]
  const firstSession = sessions[0]
  const taskCreatedAt = sessions.length > 0 ? firstSession?.created_at : null
  const premiumRequests = sessions.reduce((total, session) => total + (session.premium_requests || 0), 0)
  const {data: agentInfo} = useAgentInfo(mostRecentSession?.agent_id ?? null, mostRecentSession?.agent_slug)
  const agentProfile = useAgentProfile()
  const taskAuthor = useTaskAuthor(task, repoOwner, repoName)
  const taskOwnerLogin = taskAuthor.displayName
  const modelId = mostRecentSession?.model
  const isCopilotAgent = isCopilotCodingAgent(agentInfo?.slug)
  const canShowModel = copilotFeatureFlags.codingAgentThirdPartyModelUI || isCopilotAgent
  const resolvedModel = useSelectedModel(modelId)
  const selectedModel = canShowModel ? resolvedModel : undefined
  const {currentUser, optedInToFeedback} = useCurrentUser()
  const currentUserIsOwner = currentUser?.id === task?.creator?.id
  const separator = ' · '
  const isNarrow = useIsLayoutNarrow()
  const {openFeedback, FeedbackDialog} = useFeedbackDialog(mostRecentSession?.id)
  const feedbackButtonRef = useRef<HTMLButtonElement>(null)
  const handleOpenFeedback = useCallback(() => openFeedback(), [openFeedback])

  // Check billing usage and determine unit based on usage type (token-based or request-based)
  const currentSession = currentSessionId ? sessions.find(session => session.id === currentSessionId) : undefined
  const sessionForBilling = currentSession ?? mostRecentSession
  const isTokenBasedBilling = sessionForBilling?.usage?.type === 'token'
  const usageUnit = isTokenBasedBilling ? 'AI credits' : 'premium requests'
  const aiCredits = copilotFeatureFlags.codingAgentSessionAiCredits
    ? Math.round(((sessionForBilling?.usage?.credits ?? 0) / 1_000_000_000) * 10) / 10
    : 0
  const usageAmount = isTokenBasedBilling ? aiCredits : premiumRequests

  const feedbackActionItem = useMemo((): TaskActionItem[] => {
    if (!optedInToFeedback || !isNarrow) return []
    return [
      {
        key: 'give-feedback',
        icon: CommentIcon,
        label: 'Give feedback',
        onSelect: () => openFeedback(),
      },
    ]
  }, [optedInToFeedback, isNarrow, openFeedback])

  const extraItems = useMemo(() => [...feedbackActionItem], [feedbackActionItem])

  const [isRenaming, setIsRenaming] = useState(false)
  const moreActionsButtonRef = useRef<HTMLButtonElement>(null)

  const taskTitle = getTaskTitle(task, pull?.title, {
    preferPullTitle: isCodeReviewMode,
    formatAsReview: isCodeReviewMode,
  })

  const handleExitRename = () => {
    setIsRenaming(false)
    requestAnimationFrame(() => {
      moreActionsButtonRef.current?.focus()
    })
  }

  const handleStartRename = () => {
    setIsRenaming(true)
    sendEvent('agent_sessions.task_actions', {
      target: 'RENAME_TASK_START',
      task_id: task?.id,
    })
  }

  const effectiveBranchName = getBranchArtifactData(task?.artifacts)?.head_ref || pull?.head_ref_name || ''

  const LeadingIcon = agentProfile.slots.LeadingIcon
  const iconAttributes = useMemo(
    () =>
      LeadingIcon
        ? null
        : getLeadingVisualIconAttributes(
            pull?.state,
            pull?.inMergeQueue,
            mostRecentSession?.state,
            !!task?.archived_at,
          ),
    [pull?.state, pull?.inMergeQueue, mostRecentSession?.state, task?.archived_at, LeadingIcon],
  )

  const getTaskAuthorLogin = () => {
    if (!taskAuthor.isAutomation && currentUserIsOwner) {
      return 'You'
    }

    return taskOwnerLogin
  }

  if (isRenaming) {
    return (
      <RenameTaskHeader
        taskName={task?.name ?? 'New task'}
        displayTitle={taskTitle}
        taskId={task?.id}
        repoOwner={repoOwner}
        repoName={repoName}
        onExit={handleExitRename}
      />
    )
  }

  const actions = (
    <>
      {optedInToFeedback && !isNarrow && (
        <Button ref={feedbackButtonRef} variant="link" className="f6" onClick={handleOpenFeedback}>
          Give feedback
        </Button>
      )}
      <FeedbackDialog returnFocusRef={isNarrow ? moreActionsButtonRef : feedbackButtonRef} />
      <TaskActions
        extraItems={extraItems}
        ariaLabel="More actions"
        testId="more-actions-button"
        overlayAlign="end"
        anchorRef={moreActionsButtonRef}
        state={mostRecentSession?.state}
        taskId={task?.id}
        repoOwner={repoOwner}
        repoName={repoName}
        currentUserIsOwner={currentUserIsOwner}
        archivedAt={task?.archived_at}
        eventType={firstSession?.event_type}
        eventIdentifiers={firstSession?.event_identifiers}
        pull={pull}
        hideCopy={agentProfile.privateSessions}
        hideShare={!agentProfile.privateSessions}
        hideArchive={hideArchive}
        interactiveSession={agentProfile.interactiveSession}
        agentSlug={agentProfile.slug}
        hidePull={isCodeReviewMode}
        onRename={handleStartRename}
        sharingStatus={task?.sharing_status}
      />
      {isCodeReviewMode ? (
        pull?.url ? (
          <PullRequestButton pullUrl={pull.url} taskId={task?.id} repoNwo={pull.repository_nwo} />
        ) : null
      ) : (
        <>
          {effectiveBranchName && (
            <OpenInMenuV2
              branchName={effectiveBranchName}
              pull={pull}
              taskId={task?.id}
              isCurrentUserTaskCreator={currentUserIsOwner}
            />
          )}
        </>
      )}
    </>
  )
  const narrowActionsRow = <div className={styles.narrowActionsRow}>{actions}</div>

  return (
    <>
      {isNarrow ? narrowActionsRow : null}
      <PageHeader aria-label="Agent session header">
        <PageHeader.TitleArea className={styles.headerTitleArea}>
          <PageHeader.Title as="h1" className={styles.headerTitle}>
            {taskTitle}
          </PageHeader.Title>
        </PageHeader.TitleArea>
        <PageHeader.Description>
          <div className={styles.headerDescriptionContent}>
            <div className={styles.headerStatus}>
              {LeadingIcon ? (
                <LeadingIcon
                  state={mostRecentSession?.state}
                  prState={pull?.state}
                  prInMergeQueue={pull?.inMergeQueue}
                  isArchived={!!task?.archived_at}
                  session={mostRecentSession}
                  staleAt={task?.stale_at}
                  dataUpdatedAt={dataUpdatedAt}
                  sharingStatus={task?.sharing_status}
                />
              ) : (
                iconAttributes && <Icon icon={iconAttributes.icon} color={iconAttributes.color} />
              )}
              <span className={styles.headerUser}>{getTaskAuthorLogin()}</span>
              <span>created the session</span>
              {taskCreatedAt && <RelativeTime date={new Date(taskCreatedAt)} tense="past" />}
              <span>{separator}</span>
              {selectedModel && (
                <span className={styles.modelDisplay}>
                  {selectedModel.logoURL ? (
                    <GitHubAvatar
                      alt={`${selectedModel.vendor} logo`}
                      square
                      size={18}
                      src={selectedModel.logoURL}
                      className={styles.modelIcon}
                    />
                  ) : (
                    <CopilotIcon className={styles.modelIcon} />
                  )}
                  <span>
                    {selectedModel.displayName}
                    {separator}
                  </span>
                </span>
              )}
              <span>
                {sessions.length} {pluralize('session', sessions.length)}
              </span>
              {usageAmount > 0 && (
                <span>
                  {separator}
                  {usageAmount} {usageUnit}
                </span>
              )}
              {pull?.head_sha && pull?.repository_nwo && (
                <span style={{marginLeft: 'var(--base-size-4)'}}>
                  <ChecksStatusButton
                    headSha={pull.head_sha}
                    repositoryNwo={pull.repository_nwo}
                    commitAliveChannel={pull.commit_alive_channel}
                  />
                </span>
              )}
            </div>
          </div>
        </PageHeader.Description>
        {!isNarrow && <PageHeader.Actions>{actions}</PageHeader.Actions>}
      </PageHeader>
    </>
  )
}
